import React from "react";
import { useNavigate } from "react-router-dom";
import {
  QrCode,
  Smartphone,
  Users,
  BarChart3,
  Clock,
  Shield,
  ArrowRight,
  CheckCircle,
  Star,
  Menu,
  X,
  Zap,
  TrendingUp,
  Award,
} from "lucide-react";

const LandingPage = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [scrollY, setScrollY] = React.useState(0);

  React.useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const features = [
    {
      icon: QrCode,
      title: "QR Code Menus",
      description:
        "Generate unique QR codes for each table, allowing customers to access your digital menu instantly with a simple scan.",
    },
    {
      icon: Smartphone,
      title: "Mobile-First Design",
      description:
        "Beautiful, responsive menus optimized for all devices with an intuitive ordering experience that customers love.",
    },
    {
      icon: Users,
      title: "Customer Management",
      description:
        "Track customer orders, manage table assignments, and maintain comprehensive customer history and preferences.",
    },
    {
      icon: BarChart3,
      title: "Analytics Dashboard",
      description:
        "Get deep insights into your most popular items, peak hours, and customer preferences with real-time data visualization.",
    },
    {
      icon: Clock,
      title: "Real-time Orders",
      description:
        "Receive and manage orders in real-time with instant notifications, status updates, and seamless kitchen integration.",
    },
    {
      icon: Shield,
      title: "Secure & Reliable",
      description:
        "Enterprise-grade security with 99.9% uptime guarantee, ensuring your restaurant operations never miss a beat.",
    },
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      restaurant: "Bella Vista Restaurant",
      rating: 5,
      comment:
        "This platform transformed our ordering process. Orders are faster and customers love the convenience!",
    },
    {
      name: "Michael Chen",
      restaurant: "Golden Dragon Chinese",
      rating: 5,
      comment:
        "The analytics help us understand our customers better. Sales increased by 30% since we started using it.",
    },
    {
      name: "Emma Rodriguez",
      restaurant: "Café Mornings",
      rating: 5,
      comment:
        "Setup was incredibly easy. We had our QR codes ready in minutes and customers adapted quickly.",
    },
  ];

  const stats = [
    { icon: Users, value: "500+", label: "Active Restaurants" },
    { icon: TrendingUp, value: "50K+", label: "Orders Processed" },
    { icon: Award, value: "99.9%", label: "Uptime Guarantee" },
    { icon: Zap, value: "< 2s", label: "Average Load Time" },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav
        className={`fixed w-full top-0 z-50 transition-all duration-500 ${
          scrollY > 50 ? "bg-white shadow-lg" : "bg-white/95 backdrop-blur-md"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3 group cursor-pointer">
              <div className="w-11 h-11 bg-gradient-to-br from-red-600 via-red-500 to-red-700 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-red-500/50 transition-all duration-300 group-hover:scale-110">
                <QrCode className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-red-600 to-red-700 bg-clip-text text-transparent">
                MenuQR
              </span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-1">
              {["Services", "About", "Contact"].map((item, i) => (
                <button
                  key={i}
                  onClick={() =>
                    navigate(
                      item === "Services" ? "/" : `/${item.toLowerCase()}`
                    )
                  }
                  className="px-5 py-2 text-sm font-semibold text-gray-700 hover:text-red-600 transition-all duration-200 relative group"
                >
                  {item}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-red-600 group-hover:w-full transition-all duration-300"></span>
                </button>
              ))}
            </div>

            <div className="hidden md:flex items-center space-x-3">
              <button
                onClick={() => navigate("/login")}
                className="px-6 py-2.5 text-sm font-semibold text-gray-700 hover:text-red-600 transition-all duration-200"
              >
                Login
              </button>
              <button
                onClick={() => navigate("/register")}
                className="px-6 py-2.5 bg-gradient-to-r from-red-600 to-red-700 text-white text-sm font-semibold rounded-lg hover:shadow-lg hover:shadow-red-600/50 transition-all duration-300 transform hover:-translate-y-0.5"
              >
                Get Started
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-gray-700" />
              ) : (
                <Menu className="w-6 h-6 text-gray-700" />
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200 animate-fadeIn">
              <div className="flex flex-col space-y-2">
                {["Services", "About", "Contact"].map((item, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      navigate(
                        item === "Services" ? "/" : `/${item.toLowerCase()}`
                      );
                      setMobileMenuOpen(false);
                    }}
                    className="text-left px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-red-50 hover:text-red-600 rounded-lg transition-all duration-200"
                  >
                    {item}
                  </button>
                ))}
                <div className="border-t border-gray-200 pt-4 px-4 space-y-2">
                  <button
                    onClick={() => navigate("/login")}
                    className="w-full px-6 py-3 text-sm font-semibold text-gray-700 border-2 border-gray-200 rounded-lg hover:border-red-600 hover:text-red-600 transition-all duration-200"
                  >
                    Login
                  </button>
                  <button
                    onClick={() => navigate("/register")}
                    className="w-full px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white text-sm font-semibold rounded-lg shadow-lg hover:shadow-red-600/50 transition-all duration-300"
                  >
                    Get Started
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-50 via-white to-red-50 overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="absolute top-20 right-10 w-72 h-72 bg-red-600/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-red-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fadeInUp">
              <div className="inline-flex items-center px-4 py-2 bg-red-50 border border-red-100 rounded-full shadow-sm">
                <span className="text-sm font-semibold text-red-600">
                  ✨ Transform Your Restaurant Digital Experience
                </span>
              </div>
              <h1 className="text-5xl lg:text-6xl xl:text-7xl font-bold text-gray-900 leading-tight">
                Digital Menus That
                <span className="block mt-2 bg-gradient-to-r from-red-600 via-red-500 to-red-700 bg-clip-text text-transparent">
                  Drive Results
                </span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                Streamline your restaurant operations with QR code menus,
                real-time ordering, and powerful analytics. Join 500+
                restaurants already growing with MenuQR.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <button
                  onClick={() => navigate("/register")}
                  className="group px-8 py-4 bg-gradient-to-r from-red-600 to-red-700 text-white text-base font-semibold rounded-xl hover:shadow-2xl hover:shadow-red-600/50 transition-all duration-300 flex items-center justify-center space-x-2 transform hover:-translate-y-1"
                >
                  <span>Start Free Trial</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <button
                  onClick={() => navigate("/contact")}
                  className="px-8 py-4 bg-white text-gray-700 text-base font-semibold rounded-xl border-2 border-gray-200 hover:border-red-600 hover:text-red-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  Schedule Demo
                </button>
              </div>
            </div>

            <div className="relative lg:h-[600px] flex items-center justify-center animate-fadeInRight">
              <div className="relative w-full h-full flex items-center justify-center">
                <div className="absolute inset-0 bg-gradient-to-r from-red-400 to-red-600 rounded-3xl blur-3xl opacity-20 animate-pulse"></div>
                <div className="relative bg-white rounded-3xl shadow-2xl p-8 w-full max-w-md border border-gray-100 transform hover:scale-105 transition-transform duration-500">
                  <div className="space-y-6">
                    <div className="flex items-center justify-center w-32 h-32 mx-auto bg-gradient-to-br from-red-600 via-red-500 to-red-700 rounded-2xl shadow-xl animate-bounce-slow">
                      <QrCode className="w-20 h-20 text-white" />
                    </div>
                    <div className="text-center space-y-2">
                      <h3 className="text-2xl font-bold text-gray-900">
                        Scan & Order
                      </h3>
                      <p className="text-gray-600">
                        Instant access to your digital menu
                      </p>
                    </div>
                    <div className="space-y-3">
                      {[
                        { text: "Contactless ordering", delay: "0s" },
                        { text: "Real-time updates", delay: "0.2s" },
                        { text: "Easy payment", delay: "0.4s" },
                      ].map((item, i) => (
                        <div
                          key={i}
                          className="flex items-center space-x-3 p-4 bg-gradient-to-r from-red-50 to-transparent rounded-xl border border-red-100 hover:border-red-300 transition-all duration-300 transform hover:translate-x-2"
                          style={{ animationDelay: item.delay }}
                        >
                          <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-900">
                            {item.text}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Section */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-20 animate-fadeInUp delay-500">
            {stats.map((stat, i) => (
              <div
                key={i}
                className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl hover:border-red-200 transition-all duration-300 transform hover:-translate-y-2"
              >
                <stat.icon className="w-8 h-8 text-red-600 mb-3" />
                <p className="text-3xl font-bold text-gray-900 mb-1">
                  {stat.value}
                </p>
                <p className="text-sm text-gray-600">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-red-50/30 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16 space-y-4 animate-fadeInUp">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
              Everything You Need to Go Digital
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our comprehensive platform provides all the tools you need to
              modernize your restaurant operations and delight your customers.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group bg-white p-8 rounded-2xl border-2 border-gray-100 hover:border-red-200 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 animate-fadeInUp"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-red-100 to-red-50 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-lg group-hover:shadow-red-200">
                  <feature.icon className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-red-600 transition-colors duration-300">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-gradient-to-br from-red-50 via-white to-red-50 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-red-600/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-red-500/5 rounded-full blur-3xl"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16 space-y-4 animate-fadeInUp">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
              Loved by Restaurant Owners
            </h2>
            <p className="text-xl text-gray-600">
              Join hundreds of restaurants already using MenuQR
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-red-200 transform hover:-translate-y-2 animate-fadeInUp"
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <div className="flex items-center mb-6">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-5 h-5 text-yellow-400 fill-current animate-pulse"
                      style={{ animationDelay: `${i * 0.1}s` }}
                    />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed text-base">
                  "{testimonial.comment}"
                </p>
                <div className="flex items-center space-x-4 pt-6 border-t border-gray-100">
                  <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-red-700 rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-white font-bold text-lg">
                      {testimonial.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">
                      {testimonial.name}
                    </p>
                    <p className="text-gray-600 text-sm">
                      {testimonial.restaurant}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-red-600 via-red-600 to-red-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-red-500/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-700/30 rounded-full blur-3xl animate-pulse delay-1000"></div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="space-y-8 animate-fadeInUp">
            <h2 className="text-4xl lg:text-5xl font-bold text-white leading-tight">
              Ready to Transform Your Restaurant?
            </h2>
            <p className="text-xl text-red-100 leading-relaxed">
              Join thousands of restaurants already using MenuQR to modernize
              their operations and delight their customers.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center pt-4">
              <button
                onClick={() => navigate("/register")}
                className="group px-10 py-5 bg-white text-red-600 rounded-xl font-bold text-lg hover:bg-red-50 transition-all duration-300 shadow-2xl hover:shadow-white/50 flex items-center justify-center space-x-3 transform hover:-translate-y-1"
              >
                <span>Start Your Free Trial</span>
                <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => navigate("/contact")}
                className="px-10 py-5 bg-transparent text-white rounded-xl font-bold text-lg border-2 border-white hover:bg-white hover:text-red-600 transition-all duration-300 shadow-2xl transform hover:-translate-y-1"
              >
                Contact Sales
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="space-y-4">
              <div className="flex items-center space-x-3 group cursor-pointer">
                <div className="w-11 h-11 bg-gradient-to-br from-red-600 to-red-700 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-red-600/50 transition-all duration-300">
                  <QrCode className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-bold">MenuQR</span>
              </div>
              <p className="text-gray-400 leading-relaxed">
                Empowering restaurants with modern digital solutions for better
                customer experiences.
              </p>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4">Product</h4>
              <ul className="space-y-3">
                <li>
                  <button
                    onClick={() => navigate("/")}
                    className="text-gray-400 hover:text-red-500 transition-colors duration-200"
                  >
                    Features
                  </button>
                </li>
                <li>
                  <a
                    href="#"
                    className="text-gray-400 hover:text-red-500 transition-colors duration-200"
                  >
                    Pricing
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="text-gray-400 hover:text-red-500 transition-colors duration-200"
                  >
                    Demo
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4">Company</h4>
              <ul className="space-y-3">
                <li>
                  <button
                    onClick={() => navigate("/about")}
                    className="text-gray-400 hover:text-red-500 transition-colors duration-200"
                  >
                    About
                  </button>
                </li>
                <li>
                  <a
                    href="#"
                    className="text-gray-400 hover:text-red-500 transition-colors duration-200"
                  >
                    Blog
                  </a>
                </li>
                <li>
                  <button
                    onClick={() => navigate("/contact")}
                    className="text-gray-400 hover:text-red-500 transition-colors duration-200"
                  >
                    Contact
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4">Legal</h4>
              <ul className="space-y-3">
                <li>
                  <a
                    href="#"
                    className="text-gray-400 hover:text-red-500 transition-colors duration-200"
                  >
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="text-gray-400 hover:text-red-500 transition-colors duration-200"
                  >
                    Terms of Service
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="text-gray-400 hover:text-red-500 transition-colors duration-200"
                  >
                    Cookie Policy
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">
              © 2025 MenuQR. All rights reserved.
            </p>
            <div className="flex items-center space-x-6">
              <a
                href="#"
                className="text-gray-400 hover:text-red-500 transition-colors duration-200"
              >
                Twitter
              </a>
              <a
                href="#"
                className="text-gray-400 hover:text-red-500 transition-colors duration-200"
              >
                LinkedIn
              </a>
              <a
                href="#"
                className="text-gray-400 hover:text-red-500 transition-colors duration-200"
              >
                Facebook
              </a>
            </div>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeInRight {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes bounce-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        .animate-fadeInUp {
          animation: fadeInUp 0.8s ease-out forwards;
        }
        
        .animate-fadeInRight {
          animation: fadeInRight 0.8s ease-out forwards;
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out forwards;
        }
        
        .animate-bounce-slow {
          animation: bounce-slow 3s ease-in-out infinite;
        }
        
        .delay-500 {
          animation-delay: 0.5s;
        }
        
        .delay-1000 {
          animation-delay: 1s;
        }
        
        .bg-grid-pattern {
          background-image: 
            linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
          background-size: 50px 50px;
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
